"use client";

import { INTERCOM_APP_ID } from "@/config/config";
import { userStore } from "@/shared/stores/UserStore";
import { Intercom } from "@intercom/messenger-js-sdk";
import { useTranslation } from "@cronika/locales";
import { observer } from "mobx-react-lite";
import { FC, useEffect } from "react";

interface IntercomProviderProps {
  children: React.ReactNode;
}

// Тип для window с Intercom
interface WindowWithIntercom {
  Intercom?: (command: string, config?: Record<string, unknown>) => void;
}

export const IntercomProvider: FC<IntercomProviderProps> = observer(({ children }) => {
  const user = userStore.user;
  const userFetching = userStore.userFetching;
  const { locale } = useTranslation();

  useEffect(() => {
    if (typeof window !== "undefined" && !(window as WindowWithIntercom).Intercom) {
      Intercom({
        app_id: INTERCOM_APP_ID,
        language_override: locale,
        hide_default_launcher: true,
        alignment: "right",
        vertical_padding: 20,
        horizontal_padding: 20,
      });
      (window as WindowWithIntercom).Intercom?.("hide");
    }
  }, [locale]);

  useEffect(() => {
    if (user && !userFetching && typeof window !== "undefined" && (window as WindowWithIntercom).Intercom) {
      (window as WindowWithIntercom).Intercom!("update", {
        email: user.email,
        name: user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.email,
        created_at: user.createdAt ? Math.floor(new Date(user.createdAt).getTime() / 1000) : undefined,
        language_override: locale,
        hide_default_launcher: true,
      });
      (window as WindowWithIntercom).Intercom?.("hide");
    }
  }, [locale, user, userFetching]);

  useEffect(() => {
    if (!user && !userFetching && typeof window !== "undefined" && (window as WindowWithIntercom).Intercom) {
      (window as WindowWithIntercom).Intercom!("shutdown");
    }
  }, [user, userFetching]);

  return <>{children}</>;
});
